import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    ButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    useDataProvider,
    useTranslate,
    useNotify,
    downloadCSV,
    Title,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useQuery } from '@tanstack/react-query';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import { ActiveUsersHistoryItem } from './statistics/types';
import { APP_META, pivotHistory } from './statistics/ActiveUsersHistory';
import {
    getFinnishHolidays,
    HolidayPeriod,
} from './statistics/finnishHolidays';

type GroupBy = 'day' | 'month';
type RangeMode = 'schoolyear' | 'custom';

// Shaded background bands for Finnish holidays, drawn as an SVG overlay inside
// the chart. Only meaningful on the daily (point) axis where each category is a
// single date; period edges are snapped to the nearest available date tick.
const HolidayBands = ({
    periods,
    dates,
}: {
    periods: HolidayPeriod[];
    dates: string[];
}) => {
    const { left, top, width, height } = useDrawingArea();
    const xScale = useXScale() as any;
    if (!dates.length || typeof xScale !== 'function') return null;
    const step = typeof xScale.step === 'function' ? xScale.step() : 0;
    const right = left + width;

    return (
        <g>
            {periods.map(p => {
                const first = dates.find(d => d >= p.start && d <= p.end);
                if (!first) return null;
                let last = first;
                for (let i = dates.length - 1; i >= 0; i--) {
                    if (dates[i] >= p.start && dates[i] <= p.end) {
                        last = dates[i];
                        break;
                    }
                }
                const xl = xScale(first);
                const xr = xScale(last);
                if (xl == null || xr == null) return null;
                const x = Math.max(left, xl - step / 2);
                const w = Math.min(right, xr + step / 2) - x;
                if (w <= 0) return null;
                return (
                    <g key={p.key}>
                        <rect
                            x={x}
                            y={top}
                            width={w}
                            height={height}
                            fill={p.color}
                        />
                        <text
                            x={x + 2}
                            y={top + 4}
                            fontSize={9}
                            fill="#546e7a"
                            opacity={0.85}
                            transform={`rotate(90 ${x + 2} ${top + 4})`}
                        >
                            {p.label}
                        </text>
                    </g>
                );
            })}
        </g>
    );
};

// Transparent overlay inside the chart that maps the mouse x-position to the
// nearest date index and reports it, so hovering the chart can highlight and
// scroll to the matching table row (mirror of the table -> chart hover).
const HoverCapture = ({
    dates,
    onHover,
}: {
    dates: string[];
    onHover: (index: number | null) => void;
}) => {
    const { left, top, width, height } = useDrawingArea();
    const xScale = useXScale() as any;
    if (!dates.length || typeof xScale !== 'function') return null;
    const positions = dates.map(d => xScale(d) as number);

    const handleMove = (e: React.MouseEvent<SVGRectElement>) => {
        const svg = e.currentTarget.ownerSVGElement;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const vb = svg.viewBox?.baseVal;
        const scaleX = vb && vb.width && rect.width ? vb.width / rect.width : 1;
        const x = (e.clientX - rect.left) * scaleX;
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < positions.length; i++) {
            const dist = Math.abs(positions[i] - x);
            if (dist < bestDist) {
                bestDist = dist;
                best = i;
            }
        }
        onHover(best);
    };

    return (
        <rect
            x={left}
            y={top}
            width={width}
            height={height}
            fill="transparent"
            style={{ pointerEvents: 'all' }}
            onMouseMove={handleMove}
            onMouseLeave={() => onHover(null)}
        />
    );
};

// School year (1.8. -> 31.7.) start for a given date.
const schoolYearStart = (d: Date) => {
    const y = d.getFullYear();
    return d.getMonth() + 1 >= 8 ? new Date(y, 7, 1) : new Date(y - 1, 7, 1);
};

// Date -> yyyy-mm-dd (local) for <input type="date">.
const toInputDate = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const ActiveUsersPage = () => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    const { canAccess } = useCanAccess({
        action: 'show',
        resource: 'activeusers',
    });

    const now = new Date();
    const currentSy = schoolYearStart(now).getFullYear();
    const [rangeMode, setRangeMode] = useState<RangeMode>('schoolyear');
    const [groupBy, setGroupBy] = useState<GroupBy>('day');
    const [syYear, setSyYear] = useState<number>(currentSy);
    const [visible, setVisible] = useState<Record<string, boolean>>({
        opepalvelu: true,
        opiskelija: true,
        oppilas: true,
        total: true,
    });
    const [showHolidays, setShowHolidays] = useState<boolean>(true);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    // 'chart' hover scrolls the table to the matching row; 'table' hover must not.
    const hoverSource = useRef<'chart' | 'table' | null>(null);
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
    const [customFrom, setCustomFrom] = useState<string>(
        toInputDate(schoolYearStart(now))
    );
    const [customTo, setCustomTo] = useState<string>(toInputDate(now));

    // Aikaväli lukuvuodesta tai mukautetuista päivämääristä.
    // Lähetetään pelkkä päivämäärä (YYYY-MM-DD) ilman kellonaikaa/aikavyöhykettä,
    // jotta backendin date()-vertailu osuu tarkalleen valittuihin päiviin.
    const { from, to } = useMemo(() => {
        if (rangeMode === 'custom') {
            const f = new Date(`${customFrom}T00:00:00Z`);
            const t = new Date(`${customTo}T00:00:00Z`);
            // Tyhjä tai keskeneräinen syöte -> ei kyselyä (vältetään kaatuminen).
            if (
                !customFrom ||
                !customTo ||
                isNaN(f.getTime()) ||
                isNaN(t.getTime())
            ) {
                return { from: undefined, to: undefined };
            }
            return { from: customFrom, to: customTo };
        }
        return {
            from: `${syYear}-08-01`,
            to: `${syYear + 1}-07-31`,
        };
    }, [rangeMode, customFrom, customTo, syYear]);

    const params = `filter=${JSON.stringify({ from, to, group_by: groupBy })}`;
    const { data, isPending, error } = useQuery<ActiveUsersHistoryItem[]>({
        queryKey: ['activeusershistory', params],
        queryFn: () =>
            (dataProvider as any)
                .getAny('/statistics/activeusers/history', params)
                .then((res: any) => res.data ?? res),
        enabled: canAccess === true && !!from && !!to,
        staleTime: Infinity,
    });

    const { dates, series } = useMemo(() => {
        const pivoted = pivotHistory(data ?? null, { total: visible.total });
        const shown = pivoted.series.filter(s => {
            const meta = APP_META.find(m => m.label === s.label);
            return meta ? visible[meta.name] : true;
        });
        return { dates: pivoted.dates, series: shown };
    }, [data, visible]);

    // Loma-alueet vain päivätarkkuudella (kuukausiakselille ne eivät osu).
    const holidayPeriods = useMemo(() => {
        if (groupBy !== 'day' || !dates.length) return [];
        return getFinnishHolidays(dates[0], dates[dates.length - 1]);
    }, [groupBy, dates]);

    const handleChartHover = useCallback((index: number | null) => {
        hoverSource.current = 'chart';
        setHoveredIndex(index);
    }, []);

    // Chart-originated hover scrolls the corresponding table row into view.
    useEffect(() => {
        if (hoverSource.current !== 'chart' || hoveredIndex === null) return;
        rowRefs.current[hoveredIndex]?.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
        });
    }, [hoveredIndex]);

    // School-year options from the earliest year with data (2025) up to current.
    const syOptions = useMemo(
        () =>
            Array.from(
                { length: Math.max(0, currentSy - 2025 + 1) },
                (_, i) => currentSy - i
            ),
        [currentSy]
    );

    const handleExport = () => {
        const dateHeader = translate('statistics.activeusershistory.date');
        const rows = dates.map((d, i) => {
            const row: Record<string, string | number> = { [dateHeader]: d };
            series.forEach(s => {
                row[s.label] = s.data[i] ?? 0;
            });
            return row;
        });
        jsonExport(rows, { rowDelimiter: ';' }, (err, csv) => {
            if (err) {
                notify(translate('notifications.EXPORT_ERROR'), {
                    type: 'error',
                });
                return;
            }
            // UTF-8 BOM so Excel renders Scandinavian letters correctly.
            const suffix =
                rangeMode === 'custom'
                    ? `${customFrom}_${customTo}`
                    : `${syYear}-${syYear + 1}`;
            downloadCSV(`\ufeff${csv}`, `active-users-${suffix}-${groupBy}`);
        });
    };

    if (!canAccess) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {translate('statistics.noaccess')}
            </Alert>
        );
    }

    const hasData = !!(data && data.length > 0);

    return (
        <Box sx={{ p: 2 }}>
            <Title title={translate('statistics.activeusershistory.title')} />
            <Typography
                variant="h6"
                gutterBottom
                color="text.secondary"
                sx={{ fontWeight: 'normal' }}
            >
                {translate('statistics.activeusershistory.title')}
            </Typography>
            <Card>
                {/* Yläpalkki: aikasuodattimet + Excel-vienti (OpenSearch-tyyli) */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth={false}
                        sx={{ minWidth: 150 }}
                    >
                        <InputLabel id="range-mode-label">
                            {translate('statistics.activeusershistory.range')}
                        </InputLabel>
                        <Select
                            labelId="range-mode-label"
                            value={rangeMode}
                            label={translate(
                                'statistics.activeusershistory.range'
                            )}
                            onChange={e =>
                                setRangeMode(e.target.value as RangeMode)
                            }
                        >
                            <MenuItem value="schoolyear">
                                {translate(
                                    'statistics.activeusershistory.schoolyear'
                                )}
                            </MenuItem>
                            <MenuItem value="custom">
                                {translate(
                                    'statistics.activeusershistory.custom'
                                )}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {rangeMode === 'schoolyear' ? (
                        <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth={false}
                            sx={{ minWidth: 130 }}
                        >
                            <InputLabel id="sy-label">
                                {translate(
                                    'statistics.activeusershistory.schoolyear'
                                )}
                            </InputLabel>
                            <Select
                                labelId="sy-label"
                                value={syYear}
                                label={translate(
                                    'statistics.activeusershistory.schoolyear'
                                )}
                                onChange={e =>
                                    setSyYear(Number(e.target.value))
                                }
                            >
                                {syOptions.map(y => (
                                    <MenuItem key={y} value={y}>
                                        {`${y}–${y + 1}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>
                            <TextField
                                type="date"
                                size="small"
                                fullWidth={false}
                                label={translate(
                                    'statistics.activeusershistory.from'
                                )}
                                InputLabelProps={{ shrink: true }}
                                value={customFrom}
                                onChange={e => setCustomFrom(e.target.value)}
                                sx={{ width: 160 }}
                            />
                            <TextField
                                type="date"
                                size="small"
                                fullWidth={false}
                                label={translate(
                                    'statistics.activeusershistory.to'
                                )}
                                InputLabelProps={{ shrink: true }}
                                value={customTo}
                                onChange={e => setCustomTo(e.target.value)}
                                sx={{ width: 160 }}
                            />
                        </>
                    )}

                    <ButtonGroup variant="contained" size="small">
                        <Button
                            onClick={() => setGroupBy('day')}
                            variant={
                                groupBy === 'day' ? 'contained' : 'outlined'
                            }
                        >
                            {translate('statistics.activeusershistory.daily')}
                        </Button>
                        <Button
                            onClick={() => setGroupBy('month')}
                            variant={
                                groupBy === 'month' ? 'contained' : 'outlined'
                            }
                        >
                            {translate('statistics.activeusershistory.monthly')}
                        </Button>
                    </ButtonGroup>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        {[
                            ...APP_META.map(m => ({
                                key: m.name,
                                label: m.label,
                                color: m.color,
                            })),
                            {
                                key: 'total',
                                label: 'Yhteensä',
                                color: '#616161',
                            },
                        ].map(t => (
                            <FormControlLabel
                                key={t.key}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!visible[t.key]}
                                        onChange={e =>
                                            setVisible(v => ({
                                                ...v,
                                                [t.key]: e.target.checked,
                                            }))
                                        }
                                        sx={{
                                            color: t.color,
                                            '&.Mui-checked': { color: t.color },
                                        }}
                                    />
                                }
                                label={t.label}
                            />
                        ))}
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={showHolidays}
                                disabled={groupBy !== 'day'}
                                onChange={e =>
                                    setShowHolidays(e.target.checked)
                                }
                            />
                        }
                        label={translate(
                            'statistics.activeusershistory.holidays'
                        )}
                    />

                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={!hasData}
                        sx={{ ml: 'auto' }}
                    >
                        {translate('statistics.activeusershistory.export')}
                    </Button>
                </Box>

                <CardContent>
                    {error ? (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {(error as Error).message}
                        </Alert>
                    ) : !from || !to ? (
                        <Alert severity="info" sx={{ m: 2 }}>
                            {translate(
                                'statistics.activeusershistory.pickrange'
                            )}
                        </Alert>
                    ) : isPending ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                p: 8,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : !hasData ? (
                        <Alert severity="warning" sx={{ m: 2 }}>
                            {translate('statistics.nodata')}
                        </Alert>
                    ) : (
                        <>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                {translate(
                                    'statistics.activeusershistory.chart'
                                )}
                            </Typography>
                            <LineChart
                                height={360}
                                xAxis={[{ data: dates, scaleType: 'point' }]}
                                series={series}
                            >
                                {hoveredIndex !== null &&
                                    dates[hoveredIndex] && (
                                        <ChartsReferenceLine
                                            x={dates[hoveredIndex]}
                                            lineStyle={{
                                                stroke: '#1976d2',
                                                strokeWidth: 1.5,
                                                strokeDasharray: '4 3',
                                                opacity: 0.6,
                                            }}
                                        />
                                    )}
                                {groupBy === 'day' &&
                                    showHolidays &&
                                    holidayPeriods.length > 0 && (
                                        <HolidayBands
                                            periods={holidayPeriods}
                                            dates={dates}
                                        />
                                    )}
                                <HoverCapture
                                    dates={dates}
                                    onHover={handleChartHover}
                                />
                            </LineChart>

                            <Divider sx={{ my: 2 }} />

                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                {translate(
                                    'statistics.activeusershistory.table'
                                )}
                            </Typography>
                            <TableContainer
                                component={Paper}
                                sx={{ maxHeight: 420 }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {translate(
                                                    'statistics.activeusershistory.date'
                                                )}
                                            </TableCell>
                                            {series.map(s => (
                                                <TableCell
                                                    key={s.label}
                                                    align="right"
                                                >
                                                    {s.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dates.map((d, i) => (
                                            <TableRow
                                                key={d}
                                                hover
                                                selected={i === hoveredIndex}
                                                ref={el => {
                                                    rowRefs.current[i] = el;
                                                }}
                                                onMouseEnter={() => {
                                                    hoverSource.current =
                                                        'table';
                                                    setHoveredIndex(i);
                                                }}
                                                onMouseLeave={() =>
                                                    setHoveredIndex(null)
                                                }
                                            >
                                                <TableCell>{d}</TableCell>
                                                {series.map(s => (
                                                    <TableCell
                                                        key={s.label}
                                                        align="right"
                                                    >
                                                        {(
                                                            s.data[i] ?? 0
                                                        ).toLocaleString(
                                                            'fi-FI'
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default ActiveUsersPage;
